import type { ScheduleReportFrequency, TableReportType } from '~/types/report'
import type { ServiceGrainKey, ServicePresetKey, ServiceScopeDimension } from '~/types/report-service'

export type { ServiceScopeDimension }

export const SERVICE_PRESET_KEYS: Record<TableReportType, ServicePresetKey> = {
  alert: 'alert',
  'state-assets-log': 'state_assets_log',
  'assets-registry': 'assets_registry',
  usage: 'usage',
  'user-log': 'user_log'
}

const REPORT_TYPE_BY_PRESET: Record<ServicePresetKey, TableReportType> = {
  alert: 'alert',
  state_assets_log: 'state-assets-log',
  assets_registry: 'assets-registry',
  usage: 'usage',
  user_log: 'user-log'
}

export const SERVICE_WINDOW_PRESET_BY_RANGE: Record<string, string> = {
  today: 'today',
  lastDay: 'yesterday',
  last7Days: 'last7Days',
  last30Days: 'last30Days',
  lastWeek: 'lastWeek',
  lastMonth: 'lastMonth'
}

export const SERVICE_WINDOW_PRESET_BY_FREQUENCY: Record<ScheduleReportFrequency, string> = {
  daily: 'yesterday',
  weekly: 'lastWeek',
  monthly: 'lastMonth'
}

const ASSET_SCOPE_DIMENSIONS: ServiceScopeDimension[] = ['assetTypeIds', 'groupIds', 'zoneIds', 'routeIds', 'assetIds']

export const SERVICE_SCOPE_DIMENSIONS: Record<ServicePresetKey, ServiceScopeDimension[]> = {
  alert: ASSET_SCOPE_DIMENSIONS,
  state_assets_log: ASSET_SCOPE_DIMENSIONS,
  assets_registry: ASSET_SCOPE_DIMENSIONS,
  usage: ASSET_SCOPE_DIMENSIONS,
  user_log: []
}

export const REPORT_TYPE_BY_GRAIN: Record<ServiceGrainKey, TableReportType> = {
  asset: 'state-assets-log',
  state_event: 'assets-registry',
  alert_transition: 'alert',
  audit_entry: 'user-log'
}

export const toReportTypeFromGrain = (grain: string | null | undefined): TableReportType =>
  grain && grain in REPORT_TYPE_BY_GRAIN ? REPORT_TYPE_BY_GRAIN[grain as ServiceGrainKey] : 'assets-registry'

export const toServicePresetKey = (reportType: TableReportType): ServicePresetKey => SERVICE_PRESET_KEYS[reportType]

export const getServiceScopeDimensions = (reportType: TableReportType): ServiceScopeDimension[] =>
  SERVICE_SCOPE_DIMENSIONS[toServicePresetKey(reportType)]

export const supportsServiceScope = (reportType: TableReportType): boolean =>
  getServiceScopeDimensions(reportType).length > 0

export const toReportTypeFromPreset = (presetKey: string | null | undefined): TableReportType | undefined =>
  presetKey && presetKey in REPORT_TYPE_BY_PRESET ? REPORT_TYPE_BY_PRESET[presetKey as ServicePresetKey] : undefined

const RETIRED_PRESET_TITLE_KEYS: Record<string, string> = {
  asset_log: 'reports.downloads.retiredTypes.assetLog'
}

export const toReportTitleKeyFromPreset = (presetKey: string | null | undefined): string | undefined => {
  if (!presetKey) return undefined
  const reportType = toReportTypeFromPreset(presetKey)
  return reportType ? `reports.types.${reportType}.title` : RETIRED_PRESET_TITLE_KEYS[presetKey]
}

export const toServiceWindowPreset = (rangePreset: string): string =>
  SERVICE_WINDOW_PRESET_BY_RANGE[rangePreset] ?? rangePreset
